import { DispatchContext, StateContext } from "@client/pages/round/Round";
import { formatMoney } from "@client/utils/formatMoney";
import {
  getActiveProposals,
  getActiveRecommenders,
  getEvaluable,
  getEvaluatorTrancheList,
  getNode,
} from "@engine/round/accessors";
import { Funder, Recommender, RoundAggregate } from "@engine/round/aggregate";
import clsx from "clsx";
import { useContext, useState } from "react";

import { Thead } from "@client/components/round/matrix/DisagreementsMatrix";
import { roundValue } from "@lib/roundValue";
import { Tranche } from "@lib/tranche";
import { Evaluable, Evaluator } from "@lib/types";
import { sum } from "lodash";
import { Link } from "wouter";
import { EditableNumberField } from "./EditableNumberField";
import { TrancheMenu } from "./TrancheMenu";

type HoverColumnEventHandlers = {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

export const defaultTrancheHeight = 0.1;

export function TrancheListEditor({
  evaluator,
  unilateralAllocations,
}: {
  evaluator: Funder | Recommender;
  unilateralAllocations: {
    [evaluableId: string]: {
      amount: number;
    };
  };
}) {
  const { round } = useContext(StateContext)!;
  const dispatch = useContext(DispatchContext);
  const [hoveredColumnIndex, setHoveredColumnIndex] = useState<number | null>(
    null,
  );

  let evaluablesOrder: string[] = [];
  const evaluatorTrancheList = getEvaluatorTrancheList(round, evaluator);
  for (const tranche of evaluatorTrancheList) {
    const sortedEntries = Object.entries(tranche.allocations).sort(
      (a, b) => b[1].amount - a[1].amount,
    );
    for (const [evaluableId, _] of sortedEntries) {
      if (
        !evaluator.evaluations[evaluableId] ||
        evaluator.evaluations[evaluableId].state !== "evaluated"
      ) {
        continue;
      }
      // only add the evaluable to the order if it hasn't been added yet
      if (!evaluablesOrder.includes(evaluableId)) {
        evaluablesOrder.push(evaluableId);
      }
    }
  }

  const evaluablesWithoutEvaluations = getEvaluablesThatCanBeEvaluatedBy(
    round,
    evaluator,
  ).filter((evaluable) => !evaluablesOrder.includes(evaluable.id));

  const trancheInfos = getTrancheInfos(
    evaluator,
    evaluatorTrancheList,
    round.holdProposalId,
  );

  function _normalizeFDVs() {
    void dispatch({
      type: "NORMALIZE_EVALUATIONS",
      evaluatorId: evaluator.id,
    });
  }

  function diminishingToColumnWidth(height?: number) {
    const minColumnWidth = 50;
    const heightToReachMaxWidth = 10;
    const maxColumnWidth = 250;
    if (!height) return minColumnWidth;

    return (
      minColumnWidth +
      (maxColumnWidth - minColumnWidth) *
        (Math.min(height, heightToReachMaxWidth) / heightToReachMaxWidth)
    );
  }

  function handleTDVChange(newTDV: number, trancheIndex: number) {
    const trancheInfo = trancheInfos[trancheIndex];

    if (trancheIndex === 0) {
      const bdv = trancheInfo.topDollarValue - trancheInfo.height;
      const newHeight = newTDV - bdv;

      void dispatch({
        type: "SET_TRANCHE_HEIGHT",
        evaluatorId: evaluator.id,
        trancheIndex,
        height: newHeight,
      });
    }

    const prevTrancheInfo = trancheInfos[trancheIndex - 1];

    const newPrevHeight = prevTrancheInfo.topDollarValue - newTDV;
    void dispatch({
      type: "SET_TRANCHE_HEIGHT",
      evaluatorId: evaluator.id,
      trancheIndex: trancheIndex - 1,
      height: newPrevHeight,
    });

    if (newTDV > trancheInfo.topDollarValue) {
      const newHeight =
        newTDV - trancheInfo.topDollarValue + trancheInfo.height;

      void dispatch({
        type: "SET_TRANCHE_HEIGHT",
        evaluatorId: evaluator.id,
        trancheIndex,
        height: newHeight,
      });
    } else {
      const newHeight =
        trancheInfo.height - (trancheInfo.topDollarValue - newTDV);

      void dispatch({
        type: "SET_TRANCHE_HEIGHT",
        evaluatorId: evaluator.id,
        trancheIndex,
        height: newHeight,
      });
    }
  }

  function hoverColumnEventHandlers(i: number): HoverColumnEventHandlers {
    return {
      onMouseEnter: () => setHoveredColumnIndex(i),
      onMouseLeave: () => setHoveredColumnIndex(null),
    };
  }

  return (
    <div className="mx-auto">
      {/* <div className="w-[202px] h-[152px] p-4 pl-5 z-20 bg-stone-100 dark:bg-[#181818]">
        <Button onClick={normalizeFDVs} classes="w-40">
          Normalize FDVs
        </Button>
      </div> */}
      {/* <div className="overflow-x-auto flex-1 w-full pt-[150px]"> */}
      {/* <div className="absolute top-0 left-[0] w-[300px] h-[85px] z-50 bg-stone-100 dark:bg-[#181818]"></div> */}
      <table className="dark:border-spacing-[2px] dark:border-separate min-h-0 overflow-scroll border-separate border-spacing-0 mt-4">
        <Thead className="light:bg-[#f5f5f4] dark:bg-[#181818]">
          <tr>
            <td />
            {evaluatorTrancheList.map((tranche, i) => (
              <td
                key={i}
                className={clsx({
                  "opacity-0": i !== hoveredColumnIndex,
                })}
                {...hoverColumnEventHandlers(i)}
              >
                <TrancheKebabMenu evaluatorId={evaluator.id} trancheIndex={i} />
              </td>
            ))}
          </tr>
          <tr className="h-10">
            <th className="sticky top-0 left-0 z-30">
              <div className="font-normal mr-8 light:bg-[#f5f5f4] dark:bg-[#181818] text-right">
                <span>Utils/$</span>
              </div>
            </th>
            {evaluatorTrancheList.map((tranche, i) => {
              if (i > 0 && trancheInfos[i - 1].isHold) {
                return <th key={i} {...hoverColumnEventHandlers(i)} />;
              }
              const isHold = trancheInfos[i].isHold;
              return (
                <th key={i} {...hoverColumnEventHandlers(i)}>
                  <div
                    className={clsx(
                      "transform w-[50px] bg-stone-200 text-black",
                      !isHold && "translate-x-[-27px]",
                    )}
                  >
                    <EditableNumberField
                      value={trancheInfos[i].topDollarValue}
                      onSubmitted={(newValue) => {
                        handleTDVChange(newValue, i);
                      }}
                      min={
                        i < trancheInfos.length - 1
                          ? trancheInfos[i + 1].topDollarValue
                          : 0
                      }
                      max={i > 0 ? trancheInfos[i - 1].topDollarValue : 1000}
                      align="text-center"
                      className="w-[50px]"
                    />
                    <span
                      style={{
                        position: "absolute",
                        left: "calc(50% - 8px)",
                        bottom: -8,
                        borderLeft: "8px solid transparent",
                        borderRight: "8px solid transparent",
                        borderTop: "8px solid rgb(231,229,228)",
                      }}
                    />
                  </div>
                </th>
              );
            })}
            {/* <th>
                <div className={"transform translate-x-[-25px] w-[50px]"}>
                  0
                </div>
              </th> */}
          </tr>
          <tr className="dark:text-stone-300 relative max-h-[250px] pl-0">
            <th className="sticky top-0 left-0 light:bg-[#f5f5f4] dark:bg-[#181818] z-30">
              <div className="font-normal pr-8 text-right">
                <span>Diminishing</span>
              </div>
            </th>
            {evaluatorTrancheList.map((tranche, colIndex) => {
              if (trancheInfos[colIndex].isHold) {
                return (
                  <th key={colIndex} {...hoverColumnEventHandlers(colIndex)} />
                );
              }
              const columnWidth = diminishingToColumnWidth(
                trancheInfos[colIndex]?.height,
              );
              return (
                <th
                  key={colIndex}
                  {...hoverColumnEventHandlers(colIndex)}
                  style={{
                    minWidth: columnWidth,
                  }}
                  className={
                    "border border-black bg-stone-200 dark:bg-custom-gray"
                  }
                >
                  <EditableNumberField
                    value={tranche.height}
                    onSubmitted={(newValue) => {
                      void dispatch({
                        type: "SET_TRANCHE_HEIGHT",
                        evaluatorId: evaluator.id,
                        trancheIndex: colIndex,
                        height: newValue,
                      });
                    }}
                    min={0}
                    max={100}
                    align="text-center"
                    className="w-[50px]"
                  />
                </th>
              );
            })}
          </tr>
          <tr className="h-2">
            <td />
            {evaluatorTrancheList.map((evaluableId, i) => (
              <td key={i} {...hoverColumnEventHandlers(i)} />
            ))}
          </tr>
        </Thead>
        <tbody className="z-0">
          {evaluablesOrder.map((evaluableId, rowIndex) => (
            <tr key={evaluableId}>
              <td
                key={evaluableId}
                className="sticky m-0 p-0 left-0 pr-2 light:bg-[#f5f5f4] dark:bg-[#181818]"
              >
                <div className="flex min-w-[200px] max-w-[300px]">
                  <span className="flex-1 truncate">
                    <LinkToEvaluable
                      evaluableId={evaluableId}
                      nodeId={evaluator.id}
                    />
                  </span>
                  {formatMoney(unilateralAllocations[evaluableId]?.amount || 0)}
                </div>
              </td>
              {evaluatorTrancheList.map((tranche, colIndex) => {
                if (trancheInfos[colIndex].isHold) {
                  if (rowIndex === 0) {
                    return (
                      <td
                        key="hold"
                        rowSpan={evaluablesOrder.length}
                        {...hoverColumnEventHandlers(colIndex)}
                        className="bg-[#cc8844]"
                      >
                        <span className="rotate-90 block text-center">
                          Hold
                        </span>
                      </td>
                    );
                  } else {
                    return null;
                  }
                }
                return (
                  <Cell
                    key={colIndex}
                    evaluatorId={evaluator.id}
                    trancheIndex={colIndex}
                    evaluableId={evaluableId}
                    allocation={tranche.allocations[evaluableId]?.amount}
                    holdProposalId={round.holdProposalId}
                    isBottomPlaced={
                      tranche.allocations[evaluableId]?.bottomPlaced || false
                    }
                    hoverEventHandlers={hoverColumnEventHandlers(colIndex)}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {evaluablesWithoutEvaluations.length > 0 && (
        <div className="mt-16">
          <h2 className="text-xl mb-4">Unevaluated</h2>
          <ul className="ml-8">
            {evaluablesWithoutEvaluations.map((evaluable) => (
              <UnevaluatedProposalListItem
                key={evaluable.id}
                evaluable={evaluable}
                evaluator={evaluator}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UnevaluatedProposalListItem({
  evaluable,
  evaluator,
}: {
  evaluable: Evaluable;
  evaluator: Evaluator;
}) {
  const dispatch = useContext(DispatchContext);

  function evaluationStart() {
    void dispatch({
      type: "START_EVALUATION",
      evaluatorId: evaluator.id,
      evaluableId: evaluable.id,
    });
  }

  return (
    <li>
      <button onClick={evaluationStart} className="cursor-pointer mr-2">
        {"+"}
      </button>
      <LinkToEvaluable evaluableId={evaluable.id} nodeId={evaluator.id} />
    </li>
  );
}

function TrancheKebabMenu({
  evaluatorId,
  trancheIndex,
}: {
  evaluatorId: string;
  trancheIndex: number;
}) {
  const dispatch = useContext(DispatchContext);

  function addTrancheBefore() {
    void dispatch({
      type: "ADD_TRANCHE",
      evaluatorId: evaluatorId,
      trancheIndex,
      trancheHeight: defaultTrancheHeight,
    });
  }

  function addTrancheAfter() {
    void dispatch({
      type: "ADD_TRANCHE",
      evaluatorId: evaluatorId,
      trancheIndex: trancheIndex + 1,
      trancheHeight: defaultTrancheHeight,
    });
  }

  function onDelete() {
    void dispatch({
      type: "REMOVE_TRANCHE",
      evaluatorId: evaluatorId,
      trancheIndex,
    });
  }

  return (
    <TrancheMenu
      addTrancheBefore={addTrancheBefore}
      addTrancheAfter={addTrancheAfter}
      deleteTranche={onDelete}
    />
  );
}

const cellStyle = "p-y-2 text-right w-[80px] max-w-[80px]";
/*function TrancheHeader({
  evaluator,
  trancheInfos,
  trancheList,
  trancheIndex,
}: {
  evaluator: Evaluator;
  trancheList: Tranche[];
  trancheInfos: TrancheInfo[];
  trancheIndex: number;
}) {
  const tranche = trancheList[trancheIndex];
  const trancheInfo = trancheInfos[trancheIndex];

  const { round } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const { getSimulationHorizon, setSimulationHorizon } = useContext(
    ClientSettingsContext,
  );
  const [modalIsOpen, setIsOpen] = useState(false);

  function onModalOpen() {
    setIsOpen(true);
  }

  function onModalClose() {
    setIsOpen(false);
  }

  function addTrancheBefore() {
    setIsOpen(false);
    void dispatch("EVALUATOR_TRANCHE_ADDED", {
      evaluatorId: evaluator.id,
      trancheIndex,
      trancheHeight: defualtTrancheHeight,
    });
  }

  function addTrancheAfter() {
    setIsOpen(false);
    void dispatch("EVALUATOR_TRANCHE_ADDED", {
      evaluatorId: evaluator.id,
      trancheIndex: trancheIndex + 1,
      trancheHeight: defualtTrancheHeight,
    });
  }

  function handleHeightChange(newHeight: number) {
    const isAdjacentToZeroHeight =
      (trancheIndex > 0 && trancheList[trancheIndex - 1].height === 0) ||
      (trancheIndex + 1 < trancheList.length &&
        trancheList[trancheIndex + 1].height === 0);
    if (newHeight === 0 && isAdjacentToZeroHeight) {
      toast.error(`You can't have consecutive rows with zero height`);
    } else {
      void dispatch("EVALUATOR_TRANCHE_HEIGHT_CHANGED", {
        evaluatorId: evaluator.id,
        trancheIndex: trancheIndex,
        trancheHeight: newHeight,
      });
    }
  }

  function onDelete() {
    void dispatch("EVALUATOR_TRANCHE_REMOVED", {
      evaluatorId: evaluator.id,
      trancheIndex,
    });
  }

  let trancheTotal = trancheInfo.trancheTotal;
  const isHoldTranche = trancheTotal === 1e12;
  if (isHoldTranche) {
    trancheTotal = 0;
  }

  let cumulativeTotal = trancheInfo.cumulativeTotal;
  if (trancheInfo.isInOrAfterHold) {
    cumulativeTotal -= 1e12;
  }

  const isInSimulationHorizon =
    !trancheInfo.isInOrAfterHold &&
    cumulativeTotal <= getSimulationHorizon(round);

  function toggleSimulationHorizon() {
    if (getSimulationHorizon(round) === cumulativeTotal) {
      setSimulationHorizon(getStartingSimulationHorizon(round));
    } else {
      setSimulationHorizon(cumulativeTotal);
    }
  }

  return (
    <td
      className={clsx(
        cellStyle,
        "bg-stone-200 dark:bg-custom-gray sticky left-0 z-10",
        "dark:text-stone-300 font-bold flex min-w-[200px] justify-between pr-2",
        " whitespace-nowrap border border-black ",
      )}
    >
      <div
        className={clsx(
          trancheInfo.isInOrAfterHold && "holdHeader",
          "min-w-[15px] cursor-pointer",
          isInSimulationHorizon && "simulationHeader",
        )}
        onClick={toggleSimulationHorizon}
      ></div>
      <div className="min-w-[24px]">
        <div
          className={clsx(modalIsOpen ? "block" : "hidden group-hover:block")}
        >
          <TrancheMenu
            addTrancheBefore={addTrancheBefore}
            addTrancheAfter={addTrancheAfter}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
            deleteTranche={onDelete}
          />
        </div>
      </div>
      <div className="min-w-[80px]">
        {
          <EditableNumberField
            className="max-w-[64px] text-right mr-2"
            value={tranche.height}
            onSubmitted={handleHeightChange}
            disabled={trancheInfo.isHold}
            min={0}
            max={1.0e12}
            isMoney={false}
          />
        }
      </div>
      <div className="flex-1">{formatMoney(trancheTotal)}</div>
    </td>
  );
}*/

function Cell({
  evaluatorId,
  trancheIndex,
  evaluableId,
  allocation,
  holdProposalId,
  isBottomPlaced,
  hoverEventHandlers,
}: {
  evaluatorId: string;
  trancheIndex: number;
  evaluableId: string;
  allocation: number | undefined;
  holdProposalId: string;
  isBottomPlaced: boolean;
  hoverEventHandlers: HoverColumnEventHandlers;
}) {
  const dispatch = useContext(DispatchContext);

  function handleAllocationChange(newAllocation: number) {
    if (
      (typeof allocation === "undefined" && newAllocation === 0) ||
      newAllocation === allocation
    ) {
      return;
    }

    void dispatch({
      type: "SET_TRANCHE_ALLOCATION",
      evaluatorId,
      trancheIndex,
      amount: newAllocation,
      evaluableId,
    });
  }

  const isHold = evaluableId === holdProposalId && allocation && allocation > 0;

  return (
    <td
      {...hoverEventHandlers}
      key={evaluableId}
      className={clsx(
        "border border-black z--10",
        !isBottomPlaced && typeof allocation !== "undefined" && allocation > 1
          ? "light:bg-violet-100 dark:bg-[#36213d]"
          : "light:bg-stone-200 dark:bg-custom-gray",
        cellStyle,
        isHold && "bg-[#530]!",
      )}
    >
      <EditableNumberField
        className={"max-w-[64px] text-right mr-2"}
        value={allocation}
        isMoney={true}
        min={0}
        max={1.0e9}
        onSubmitted={handleAllocationChange}
      />
    </td>
  );
}

type TrancheInfo = {
  isHold: boolean;
  isInOrAfterHold: boolean;
  trancheTotal: number;
  cumulativeTotal: number;
  height: number;
  topDollarValue: number;
};

function getTrancheInfos(
  evaluator: Evaluator,
  tranches: Tranche[],
  holdProposalId: string,
): TrancheInfo[] {
  const trancheInfos: TrancheInfo[] = [];
  let isAfterHoldTranche = false;
  let cumulativeTotal = 0;
  for (const tranche of tranches) {
    let trancheTotal = 0;
    for (const entry of Object.entries(tranche.allocations)) {
      const [evaluableId, { amount }] = entry;

      if (
        !evaluator.evaluations[evaluableId] ||
        evaluator.evaluations[evaluableId].state !== "evaluated"
      ) {
        continue;
      }

      trancheTotal += amount;
    }

    cumulativeTotal += trancheTotal;

    let isInOrAfterHoldTranche = isAfterHoldTranche;
    let isHoldTranche = false;
    if (!isAfterHoldTranche) {
      const nonHold = Object.entries(tranche.allocations).filter(
        ([proposalId]) => proposalId !== holdProposalId,
      );
      const amountAllocatedToNotHold = sum(nonHold.map(([, v]) => v.amount));
      const amountAllocatedToHold =
        tranche.allocations[holdProposalId]?.amount ?? 0;

      if (amountAllocatedToNotHold === 0 && amountAllocatedToHold > 0) {
        isHoldTranche = true;
        isInOrAfterHoldTranche = true;
        isAfterHoldTranche = true;
      }
    }

    trancheInfos.push({
      isHold: isHoldTranche,
      isInOrAfterHold: isInOrAfterHoldTranche,
      trancheTotal: trancheTotal,
      cumulativeTotal,
      height: tranche.height,
      topDollarValue: NaN,
    });
  }

  let cumulativeHeight = 0;
  for (let i = trancheInfos.length - 1; i >= 0; i--) {
    cumulativeHeight += trancheInfos[i].height;
    trancheInfos[i].topDollarValue = roundValue(cumulativeHeight);
  }

  return trancheInfos;
}

function LinkToEvaluable({
  evaluableId,
  nodeId,
}: {
  evaluableId: string;
  nodeId: string;
}) {
  const { round } = useContext(StateContext)!;
  const evaluable = getEvaluable(round, evaluableId);
  const evaluatorNode = getNode(round, nodeId);

  if (evaluableId === round.holdProposalId) {
    return <span>Hold</span>;
  }
  if (!evaluable) {
    return `[Invalid evaluable ID: ${evaluableId}]`;
  }

  switch (evaluable.type) {
    case "proposal":
      // round/${roundId}/allocations/${current user shortname}/evaluable/${proposal shortname}
      return (
        <Link
          href={
            `~/round/${round.id}/allocations/${evaluatorNode?.shortname}/` +
            `evaluable/${evaluable.shortname}`
          }
        >
          {evaluable.shortname}
        </Link>
      );
    case "recommender":
      return <span>{evaluable.shortname}</span>;
  }
}

function getEvaluablesThatCanBeEvaluatedBy(
  round: RoundAggregate,
  evaluator: Evaluator,
) {
  if (evaluator.type === "funder") {
    return getActiveRecommenders(round);
  } else if (evaluator.type === "recommender") {
    return getActiveProposals(round);
  } else {
    throw new Error("Not an evaluator");
  }
}
